import React, { useEffect, useState } from 'react';
import { ModalBase } from '@/components/ModalBase';

import { MemberNewsModalIsOpenAtom, MemberNewsCompleteModalIsOpenAtom, ErrorMessageAtom } from '@/utility/CommonAtoms';
import { useSetAtom, useAtomValue } from 'jotai';
import { useQuery } from '@tanstack/react-query';
import { CheckboxWithLabel } from '@/components/CheckboxWithLabel';
import { consentRegitAtoms } from '@/features/regist/hoc/RegistAtom';
import { useForm } from 'react-hook-form';

import { AuthAtoms } from '@/features/login/hoc/AuthAtom';
import { isAdminRoleAtom } from '@/stores/session/UserRoleAtom';
import { getMember, useUpdateMailLetter } from '@/apis/core/user';
import { GetMember200ResponseBody } from 'openapi/omise-craft-core-spec';
import { mailList, updateMailLetterSetting } from '@/apis/core/mailLetter';
import { GetMailLetterSetting200ResponseBody } from 'openapi/omise-craft-core-spec';
import { getMailLetterSetting } from '@/apis/core/mailLetter';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';

type Props = {
  isOpen: boolean;
};

export const MemberNewsModal = (props: Props) => {
  // 会員ではない場合、モーダルは表示させない
  const isAuth = useAtomValue(AuthAtoms.isAuth);
  const isOperator = useAtomValue(isAdminRoleAtom);
  const setMemberNewsIsOpen = useSetAtom(MemberNewsModalIsOpenAtom);
  const setNewsCompleteModalIsOpen = useSetAtom(MemberNewsCompleteModalIsOpenAtom);
  const { control } = useForm();
  const notiVersion = '1';

  const navigate = useNavigate();
  const setErrorMessage = useSetAtom(ErrorMessageAtom);

  // タイトル
  const typeTitle = [
    '',
    '新着物件お知らせ配信、スタート！',
    '「お知らせメール」配信がスタートしました！',
    '新着物件などのメール配信がスタート！'
  ];
  const categoryListProperty = [
    {
      id: 1,
      name: '「新着物件メール」を受け取る',
      explanation: '新しく追加された飲食店向け物件情報を、いち早くメールでお届けします。'
    }
  ];
  const categoryListNews = [
    {
      id: 2,
      name: '「お知らせメール」を受け取る',
      explanation: 'お得なキャンペーン情報などを飲食店開業に役立つ情報をお届けします。'
    }
  ];
  let submitPossible = false;
  const [isCheckProperty, setIscheckProperty] = useState<boolean>(false);
  const [isCheckNews, setIscheckNews] = useState<boolean>(false);
  const handleChange = (target: string, checked: boolean) => {
    if (target === 'property') {
      setIscheckProperty(checked);
    } else if (target === 'news') {
      setIscheckNews(checked);
    }
    isCheckProperty || isCheckNews ? (submitPossible = true) : (submitPossible = false);
  };

  const submitPossibleClass = () => {
    let addClass;
    isCheckProperty || isCheckNews
      ? (addClass = ' font-bold md:w-grid-small w-grid-4col h-[40px] rounded-[25px] bg-main text-white')
      : (addClass =
          ' font-bold md:w-grid-small w-grid-4col h-[40px] rounded-[25px] bg-disabled text-sub  cursor-default');
    return addClass;
  };

  // 会員情報の取得（非ログイン時は呼ばない）
  const [memberData, setMemberData] = useState<GetMember200ResponseBody | undefined>(undefined);

  // メール配信フラグ
  const { data: propertyMailData, isSuccess: propertyMailDataSuccess } = useQuery<
    GetMailLetterSetting200ResponseBody,
    Error
  >(['get_mail_letter_setting'], () => getMailLetterSetting(), {
    enabled: !isOperator,
    refetchOnWindowFocus: false,
    retry: false
  });

  const [settingsProperty, setSettingsProperty] = useState<boolean>();

  const settingPropetyMail = async () => {
    const updateSettingsProperty = !settingsProperty;
    setSettingsProperty(updateSettingsProperty);

    try {
      // 情報更新
      const setteingListParam: mailList = {
        column_flg: false,
        seminar_flg: false,
        campaign_flg: false,
        property_flg: updateSettingsProperty !== undefined ? updateSettingsProperty : false,
        property_init_flg: true,
        property_line_messaging_flg:
          propertyMailData?.property_line_messaging_flg !== undefined
            ? propertyMailData.property_line_messaging_flg
            : false
      };
      setMemberNewsIsOpen(false);
      await updateMailLetterSetting(undefined, setteingListParam);

      // comp modal open
      setNewsCompleteModalIsOpen(true);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        console.error('Error details:', {
          status: error.response?.status,
          data: error.response?.data,
          url: error.config?.url
        });

        setErrorMessage(error.response?.data?.message);
        navigate('/error');
      } else {
        setErrorMessage('エラーが発生しました');
        navigate('/error');
      }
    }
  };

  const handleSettingPropetyMail = async () => {
    settingPropetyMail();

    // GAクリックイベント送信
    ReactGA.gtag('event', 'click_button', {
      action: 'click',
      event_category: 'click_button',
      event_label: 'オプトイン誘導モーダル_「新着物件メール」を受け取る'
    });
  };

  const updateMailLetterMutation = useUpdateMailLetter(true);
  // お知らせメール設定
  const settingNewsMail = async () => {
    if (!isOperator) {
      try {
        setMemberNewsIsOpen(false);
        await updateMailLetterMutation.mutateAsync();

        setNewsCompleteModalIsOpen(true);
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          console.error('Error details:', {
            status: error.response?.status,
            data: error.response?.data,
            url: error.config?.url
          });

          setErrorMessage(error.response?.data?.message);
          navigate('/error');
        } else {
          setErrorMessage('エラーが発生しました');
          navigate('/error');
        }
      }
    }
  };

  const handleSettingNewsMail = async () => {
    settingNewsMail();

    // GAクリックイベント送信
    ReactGA.gtag('event', 'click_button', {
      action: 'click',
      event_category: 'click_button',
      event_label: 'オプトイン誘導モーダル_「お知らせメール」を受け取る'
    });
  };

  const handleSettingAllMail = async () => {
    if (isCheckProperty && isCheckNews && !isOperator) {
      const updateSettingsProperty = !settingsProperty;
      setSettingsProperty(updateSettingsProperty);
      try {
        // 情報更新
        const setteingListParam: mailList = {
          column_flg: false,
          seminar_flg: false,
          campaign_flg: false,
          property_flg: updateSettingsProperty !== undefined ? updateSettingsProperty : false,
          property_init_flg: true,
          property_line_messaging_flg:
            propertyMailData?.property_line_messaging_flg !== undefined
              ? propertyMailData.property_line_messaging_flg
              : false
        };
        setMemberNewsIsOpen(false);
        await updateMailLetterSetting(undefined, setteingListParam);
        await updateMailLetterMutation.mutateAsync();

        // comp modal open
        setNewsCompleteModalIsOpen(true);
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          console.error('Error details:', {
            status: error.response?.status,
            data: error.response?.data,
            url: error.config?.url
          });

          setErrorMessage(error.response?.data?.message);
          navigate('/error');
        } else {
          setErrorMessage('エラーが発生しました');
          navigate('/error');
        }
      }
    } else if (isCheckProperty && !isOperator) {
      settingPropetyMail();
    } else if (isCheckNews && !isOperator) {
      settingNewsMail();
    }

    // GAクリックイベント送信
    ReactGA.gtag('event', 'click_button', {
      action: 'click',
      event_category: 'click_button',
      event_label: 'オプトイン誘導モーダル_決定'
    });
  };

  // メールの承認状態によって表示内容変更
  const [newsType, setNewsType] = useState(0);

  useEffect(() => {
    // reset
    setMemberNewsIsOpen(false);
    setNewsCompleteModalIsOpen(false);

    const fetchMemberData = async () => {
      if (!isOperator) {
        try {
          setMemberData(await getMember(undefined, true));

          if (memberData?.mail_agreed_at && propertyMailData?.property_flg) {
            // お知らせ許可 & 物件メール許可
            setMemberNewsIsOpen(false);
            setNewsType(0);
            return;
          } else if (memberData?.mail_agreed_at && !propertyMailData?.property_flg) {
            // お知らせ許可 & 物件メール未許可
            setMemberNewsIsOpen(false);
            setNewsType(1);
          } else if (!memberData?.mail_agreed_at && propertyMailData?.property_flg) {
            // お知らせ未許可 & 物件メール許可
            setMemberNewsIsOpen(false);
            setNewsType(2);
          } else if (!memberData?.mail_agreed_at && !propertyMailData?.property_flg) {
            // お知らせ未許可 & 物件メール未許可
            setMemberNewsIsOpen(false);
            setNewsType(3);
          }

          if (String(memberData?.featureNotiVersion) === String(notiVersion)) {
            if (isAuth && newsType !== 0) {
              setMemberNewsIsOpen(true);
            } else {
              setMemberNewsIsOpen(false);
            }
          } else {
            setMemberNewsIsOpen(false);
          }
        } catch (error) {
          console.error('Failed to fetch member data:', error);
        }
      }
    };
    fetchMemberData();
  }, [propertyMailData && propertyMailDataSuccess && memberData?.id]);

  // お知らせコンテンツ部分表示内容
  const childrenContent = () => {
    switch (newsType) {
      case 1:
        return (
          <div className="mt-[20px] text-left">
            <p className="text-xl">
              新しく追加された飲食店向け物件情報を、いち早くメールでお届けします！
              <br />
              理想の物件を見逃さないために、ぜひご登録ください。
            </p>
            <div className="mt-[15px]">
              <button
                onClick={() => {
                  handleSettingPropetyMail();
                }}
                className="btnModalGreen md:w-auto w-full"
              >
                「新着物件メール」を受け取る
              </button>
            </div>
            <p className="mt-[15px] md;text-m text-base text-gray-500">
              配信の設定や解除はいつでも可能です。
              <br />
              <a href="/mypage/mail_letter" className="text-blue-500 underline">
                [配信の設定はこちら]
              </a>
              <br />
              ※初期登録時は物件配信の希望エリアが“全エリア“で設定されます。
            </p>
          </div>
        );
        break;
      case 2:
        return (
          <div className="mt-[20px] text-left">
            <p className="text-xl">
              お得なキャンペーン情報や最新コラム、セミナー開催情報などを飲食店開業に役立つ情報をお届けします。あなたにぴったりの情報を見逃さないために、是非ご登録ください。
            </p>
            <div className="mt-[15px]">
              <button
                onClick={() => {
                  handleSettingNewsMail();
                }}
                className="btnModalGreen md:w-auto w-full"
              >
                「お知らせメール」を受け取る
              </button>
            </div>
            <p className="mt-[25px] md;text-m text-base text-gray-500">
              配信の設定や解除はいつでも可能です。
              <br />
              <a href="/mypage/mail_letter" className="text-blue-500 underline">
                [配信の設定はこちら]
              </a>
              <br />
              ※システム上、設定の反映に数日かかる場合がございます。
            </p>
          </div>
        );

        break;
      case 3:
        return (
          <div className="mt-[20px] text-left md:overflow-y-hidden">
            <div className="short:overflow-y-scroll overflow-y-hidden md:max-h-auto max-h-[calc(100vh_-_310px)] visible-scrollbar pr-[4px]">
              <p className="text-xl">
                最新情報をお送りするメール配信がそれぞれスタートしました。
                <br />
                あなたにぴったりの情報を見逃さないために、ぜひご登録ください！
              </p>
              <div className="mt-[15px] px-0 py-[15px]">
                <CheckboxWithLabel
                  label=""
                  useLabel={false}
                  list={categoryListProperty}
                  listClass="gap-[10px] w-full"
                  listLabelClass="hidden"
                  inputAreaWidth="!mb-0 pr-[10px] newsModalCheckbox bg-white rounded-[10px] !bg-background !h-auto ml-0 mr-0 w-full "
                  atom={consentRegitAtoms.category}
                  name="category"
                  control={control}
                  onSingleCheckChanged={(e) => handleChange('property', e)}
                />
                <CheckboxWithLabel
                  label=""
                  areaClass="mt-[15px]"
                  useLabel={false}
                  list={categoryListNews}
                  listClass="gap-[10px] w-full"
                  listLabelClass="hidden"
                  inputAreaWidth="!mb-0 pr-[10px] newsModalCheckbox bg-white rounded-[10px] !bg-background !h-auto ml-0 mr-0 w-full "
                  atom={consentRegitAtoms.category}
                  name="category"
                  control={control}
                  onSingleCheckChanged={(e) => handleChange('news', e)}
                />
              </div>
              <div>
                <p className="mt-[25px] md;text-m text-base text-gray-500">
                  配信の設定や解除はいつでも可能です。
                  <br />
                  <a href="/mypage/mail_letter" className="text-blue-500 underline">
                    [配信の設定はこちら]
                  </a>
                </p>
              </div>
            </div>
            <div className="mt-[24px] flex justify-center gap-[30px] md:flex-nowrap flex-wrap">
              <button
                id="propertySelectDeleteComplete"
                className={submitPossibleClass()}
                onClick={() => handleSettingAllMail()}
                disabled={submitPossible}
              >
                決定する
              </button>
              <button
                id="propertySelectDeleteComplete"
                className=" font-bold md:w-grid-small w-grid-4col h-[40px] rounded-[25px] bg-background text-main"
                onClick={() => setMemberNewsIsOpen(false)}
              >
                閉じる
              </button>
            </div>
          </div>
        );
        break;
      default:
        '';
    }
  };

  // お知らせ完了コンテンツ部分表示内容
  const completeChildrenContent = () => {
    switch (newsType) {
      case 1:
        return (
          <div>
            <p className="mt-[15px] md;text-m text-base text-gray-500">
              配信の設定や解除はいつでも可能です。
              <br />
              <a href="/mypage/mail_letter" className="text-blue-500 underline">
                [配信の設定はこちら]
              </a>
              <br />
              ※初期登録時は物件配信の希望エリアが“全エリア“で設定されます。
            </p>
          </div>
        );
        break;
      case 2:
        return (
          <div>
            <p className="mt-[15px] md;text-m text-base text-gray-500">
              配信の設定や解除はいつでも可能です。
              <br />
              <a href="/mypage/mail_letter" className="text-blue-500 underline">
                [配信の設定はこちら]
              </a>
              <br />
              ※システム上、設定の反映に数日かかる場合がございます。
            </p>
          </div>
        );
        break;
      case 3:
        return (
          <div>
            <p className="mt-[15px] md;text-m text-base text-gray-500">
              配信の設定や解除はいつでも可能です。
              <br />
              <a href="/mypage/mail_letter" className="text-blue-500 underline">
                [配信の設定はこちら]
              </a>
            </p>
          </div>
        );
        break;
      default:
        '';
    }
  };

  const modalTitle = typeTitle[newsType];
  const isOpenModal = props.isOpen;

  if (isAuth || newsType !== 0) {
    return (
      <>
        {isOpenModal ? (
          <>
            <ModalBase
              modalOpenAtom={MemberNewsModalIsOpenAtom}
              modalClass="memberNewsModal md:!w-auto max-w-[770px] xl:min-w-[550px] "
              backgroundModalClose={true}
            >
              <div className="inline-block mx-auto">
                <h2 className="mt-[-35px] text-main text-left">{modalTitle ?? ''}</h2>
                {childrenContent()}
              </div>
            </ModalBase>
            <ModalBase
              modalOpenAtom={MemberNewsCompleteModalIsOpenAtom}
              modalClass="completeModal md:!w-auto max-w-[770px] xl:min-w-[550px] "
            >
              <>
                <div>
                  <div className="inline-block mx-auto">
                    <h2 className="text-main mt-[-35px] text-left">設定が完了しました</h2>
                    <div className="mt-[20px] text-left font-normal text-[20px]">
                      <p>
                        ご登録ありがとうございます！
                        <br />
                        ご登録されているメールアドレスに送付いたします。
                      </p>
                      {completeChildrenContent()}
                    </div>
                  </div>
                </div>
                <div className="md:flex md:justify-center block space-content-wide-t">
                  <button
                    onClick={() => setNewsCompleteModalIsOpen(false)}
                    id="save"
                    className=" font-bold md:w-grid-small w-grid-4col h-[40px] rounded-[25px] bg-background text-main"
                  >
                    閉じる
                  </button>
                </div>
              </>
            </ModalBase>
          </>
        ) : (
          ''
        )}
      </>
    );
  } else {
    return false;
  }
};
